/**
 * Navigation Component
 *
 * Persistent navigation bar for authenticated pages
 * Shows on all protected routes with links to main sections
 */

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canManageTeam, canAccessSettings, canViewAllCustomers, getRoleLabel } from '../utils/permissions';
import MobileMenu from './MobileMenu';
import logo from '../assets/logo.png';

export default function Navigation() {
  const { user, contractor, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Helper function to check if link is active
  const isActive = (path) => {
    // Exact match for these paths to avoid conflicts
    if (path === '/dashboard' || path === '/assessment') {
      return location.pathname === path;
    }
    // For other paths, check if current path starts with the link path
    return location.pathname.startsWith(path);
  };

  const navLinkStyle = (path) => ({
    padding: '10px 16px',
    borderRadius: '6px',
    textDecoration: 'none',
    color: isActive(path) ? '#667eea' : '#2c3e50',
    background: isActive(path) ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
    fontWeight: isActive(path) ? '600' : '500',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap'
  });

  return (
    <>
      <MobileMenu />
      <nav style={{
        background: 'white',
        borderBottom: '2px solid #e0e6ed',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div className="container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Logo / Brand */}
          <Link to="/dashboard" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
            marginRight: '30px'
          }}>
            <img
              src={logo}
              alt="Krinos Logo"
              style={{ height: '36px', width: 'auto' }}
            />
            <span style={{
              fontSize: '22px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Krinos
            </span>
          </Link>

          {/* Main Navigation Links */}
          <div
            className="desktop-nav"
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flex: 1,
              flexWrap: 'wrap'
            }}>
          <Link to="/dashboard" style={navLinkStyle('/dashboard')}>
            🏠 Dashboard
          </Link>
          <Link to="/assessment" style={navLinkStyle('/assessment')}>
            🔍 New Assessment
          </Link>
          <Link to="/assessments" style={navLinkStyle('/assessments')}>
            📋 Assessments
          </Link>
          {canViewAllCustomers(user) && (
            <Link to="/customers" style={navLinkStyle('/customers')}>
              👤 Customers
            </Link>
          )}
          {canManageTeam(user) && (
            <Link to="/team" style={navLinkStyle('/team')}>
              👥 Team
            </Link>
          )}
          {canAccessSettings(user) && (
            <Link to="/settings" style={navLinkStyle('/settings')}>
              ⚙️ Settings
            </Link>
          )}
        </div>

        {/* User Info & Logout */}
        <div
          className="desktop-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginLeft: '20px'
          }}>
          <div style={{
            fontSize: '13px',
            color: '#64748b',
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}>
            <div style={{ fontWeight: '600', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
              {user?.full_name || user?.email}
              <span style={{
                padding: '2px 6px',
                background: user?.role === 'owner' ? '#10b981' : user?.role === 'admin' ? '#f59e0b' : '#64748b',
                color: 'white',
                fontSize: '10px',
                fontWeight: '600',
                borderRadius: '4px',
                textTransform: 'uppercase'
              }}>
                {getRoleLabel(user)}
              </span>
            </div>
            {contractor?.company_name && (
              <div style={{ fontSize: '12px' }}>
                {contractor.company_name}
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="btn btn-secondary"
            style={{ fontSize: '13px', padding: '8px 14px' }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </nav>
    </>
  );
}
